import React, { useRef, useEffect, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Box, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VenueBackground from './VenueBackground';
import TableElement from './TableElement';
import ModuleElement from './ModuleElement';
import { PALETTE_MODULES } from './constants';

/**
 * Floor plan canvas – rendering only, no DndContext here.
 * The DndContext lives in SeatingTab so it can also cover the toolbar palette
 * and the guest list panel (which are siblings of this component).
 *
 * Props:
 *   canvasRef        – forwarded ref for the outer container (used for coordinate calc)
 *   layout, seatingChart, zoom, pan, activeFilter
 *   activeDragData   – current drag payload (from SeatingTab)
 *   selectedElementId, onSelectElement
 *   highlightedGuestId
 *   onResizeElement, onDeleteElement, onRenameTable, onTableClick
 *   onRotateElement  – (id, angle, isTable) => void
 *   polygonEditMode, onPolygonChange
 */
export default function FloorPlanCanvas({
  canvasRef,
  layout,
  seatingChart,
  zoom,
  pan,
  onZoomChange,
  onPanChange,
  onPinchChange,
  activeFilter,
  activeDragData,
  selectedElementId,
  onSelectElement,
  highlightedGuestId,
  onResizeElement,
  onDeleteElement,
  onRenameTable,
  onTableClick,
  onRotateElement,
  polygonEditMode,
  onPolygonChange,
}) {
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Refs for stable callback access — touch listeners run once, values always current
  const panRef = useRef(pan);
  useEffect(() => { panRef.current = pan; }, [pan]);
  const onPinchChangeRef = useRef(onPinchChange);
  useEffect(() => { onPinchChangeRef.current = onPinchChange; }, [onPinchChange]);
  const onPanChangeRef = useRef(onPanChange);
  useEffect(() => { onPanChangeRef.current = onPanChange; }, [onPanChange]);
  const polygonEditModeRef = useRef(polygonEditMode);
  useEffect(() => { polygonEditModeRef.current = polygonEditMode; }, [polygonEditMode]);

  // ─── Zoom with mouse wheel ──────────────────────────────────────────────────
  useEffect(() => {
    const el = canvasRef?.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const pivotX = e.clientX - rect.left;
      const pivotY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      onZoomChange({ delta, pivotX, pivotY });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [canvasRef, onZoomChange]);

  // ─── Pan with middle mouse OR left-click on empty canvas ────────────────────
  useEffect(() => {
    const el = canvasRef?.current;
    if (!el) return;

    const startPan = (e) => {
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    };

    const onMouseDown = (e) => {
      if (e.button === 1) {
        e.preventDefault();
        startPan(e);
        return;
      }
      // Left-click on canvas background (not on a draggable element) → pan
      if (e.button === 0 && !e.target.closest('[data-dnd-draggable]') && !polygonEditMode) {
        startPan(e);
      }
    };
    const onMouseMove = (e) => {
      if (!isPanning.current) return;
      onPanChange({
        x: panStart.current.panX + (e.clientX - panStart.current.x),
        y: panStart.current.panY + (e.clientY - panStart.current.y),
      });
    };
    const onMouseUp = () => { isPanning.current = false; };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [canvasRef, pan, onPanChange, polygonEditMode]);

  // ─── Touch: pinch-zoom + single-finger pan ───────────────────────────────────
  // Uses native touch events (not pointer events) to avoid dnd-kit conflicts.
  // All mutable values accessed via refs so this effect runs only once — no
  // listener churn on every state update (which caused the choppy pan).
  useEffect(() => {
    const el = canvasRef?.current;
    if (!el) return;

    const touches = {}; // touch.identifier → {x, y}
    let lastPinchDist = null;
    let lastMidX = null;
    let lastMidY = null;
    let panGesture = null; // {startX, startY, startPanX, startPanY}
    let rafId = null;

    const onTouchStart = (e) => {
      Array.from(e.changedTouches).forEach(t => {
        touches[t.identifier] = { x: t.clientX, y: t.clientY };
      });
      const ids = Object.keys(touches);

      if (ids.length >= 2) {
        // Two-finger pinch — prevent browser zoom + pointer-event synthesis
        e.preventDefault();
        panGesture = null;
        const [p0, p1] = [touches[ids[0]], touches[ids[1]]];
        lastPinchDist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
        lastMidX = (p0.x + p1.x) / 2;
        lastMidY = (p0.y + p1.y) / 2;
      } else if (ids.length === 1) {
        const t = e.changedTouches[0];
        if (!t.target.closest('[data-dnd-draggable]') && !polygonEditModeRef.current) {
          // Canvas background — prevent page scroll and dnd-kit interference
          e.preventDefault();
          panGesture = {
            startX: t.clientX, startY: t.clientY,
            startPanX: panRef.current.x, startPanY: panRef.current.y,
          };
        }
        // On a table/element: don't preventDefault → pointer events fire → dnd-kit drags
      }
    };

    const onTouchMove = (e) => {
      Array.from(e.changedTouches).forEach(t => {
        if (touches[t.identifier] !== undefined) {
          touches[t.identifier] = { x: t.clientX, y: t.clientY };
        }
      });
      const ids = Object.keys(touches);

      if (ids.length >= 2 && lastPinchDist !== null) {
        e.preventDefault();
        const [p0, p1] = [touches[ids[0]], touches[ids[1]]];
        const newDist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
        const newMidX = (p0.x + p1.x) / 2;
        const newMidY = (p0.y + p1.y) / 2;
        if (lastPinchDist > 0) {
          const rect = el.getBoundingClientRect();
          const payload = {
            delta: newDist / lastPinchDist,
            pivotX: lastMidX - rect.left,
            pivotY: lastMidY - rect.top,
            midDeltaX: newMidX - lastMidX,
            midDeltaY: newMidY - lastMidY,
          };
          if (rafId) cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(() => { onPinchChangeRef.current(payload); rafId = null; });
        }
        lastPinchDist = newDist;
        lastMidX = newMidX;
        lastMidY = newMidY;
      } else if (ids.length === 1 && panGesture) {
        e.preventDefault();
        const t = e.changedTouches[0];
        const payload = {
          x: panGesture.startPanX + (t.clientX - panGesture.startX),
          y: panGesture.startPanY + (t.clientY - panGesture.startY),
        };
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => { onPanChangeRef.current(payload); rafId = null; });
      }
    };

    const onTouchEnd = (e) => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      Array.from(e.changedTouches).forEach(t => { delete touches[t.identifier]; });
      if (Object.keys(touches).length < 2) { lastPinchDist = null; }
      if (Object.keys(touches).length === 0) { panGesture = null; }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [canvasRef]); // Runs once — all mutable values are accessed via refs

  // ─── Droppable canvas zone ───────────────────────────────────────────────────
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: 'canvas' });

  // ─── Trash zone (shown when dragging a canvas element) ──────────────────────
  const { setNodeRef: setTrashRef, isOver: isOverTrash } = useDroppable({ id: 'trash' });
  const isDraggingCanvasElement = activeDragData?.type === 'CANVAS_ELEMENT';

  const dbTableMap = {};
  (seatingChart?.tables ?? []).forEach(t => { dbTableMap[t.id] = t; });

  // highlightedGuestId lehet egy azonosító vagy tömb (a kereső több találatot is kiemel)
  const highlightedIds = new Set(
    Array.isArray(highlightedGuestId) ? highlightedGuestId : highlightedGuestId != null ? [highlightedGuestId] : []
  );
  const highlightedTableIds = new Set(
    (seatingChart?.tables ?? [])
      .filter(t => t.guests?.some(g => highlightedIds.has(g.id)))
      .map(t => t.id)
  );

  return (
    <Box
      ref={canvasRef}
      sx={{
        flex: 1,
        overflow: 'hidden',
        bgcolor: '#f0ede6',
        position: 'relative',
        cursor: 'default',
        touchAction: 'none',
      }}
      onClick={() => { if (!polygonEditMode) onSelectElement(null); }}
    >
      {/* Full-size droppable overlay (receives palette drops) */}
      <Box
        ref={setDropRef}
        sx={{ position: 'absolute', inset: 0 }}
      >
        {/* Scaled/panned canvas content */}
        <Box
          sx={{
            position: 'absolute',
            transformOrigin: '0 0',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            width: layout.venue.width,
            height: layout.venue.height,
          }}
        >
          <VenueBackground
            venueWidth={layout.venue.width}
            venueHeight={layout.venue.height}
            venuePolygon={layout.venue.polygon ?? null}
            editingPolygon={polygonEditMode}
            zoom={zoom}
            onPolygonChange={onPolygonChange}
          />

          {/* Non-table elements */}
          {layout.elements.map(el => (
            <ModuleElement
              key={el.id}
              element={el}
              zoom={zoom}
              isSelected={selectedElementId === `module-${el.id}`}
              onClick={() => onSelectElement(`module-${el.id}`)}
              onDelete={() => onDeleteElement(el.id, false)}
              onResize={(nx, ny, nw, nh) => onResizeElement(el.id, nx, ny, nw, nh, false)}
              onResizeEnd={(nx, ny, nw, nh) => onResizeElement(el.id, nx, ny, nw, nh, false)}
              onRotate={(angle) => onRotateElement?.(el.id, angle, false)}
            />
          ))}

          {/* Table elements */}
          {Object.entries(layout.tables).map(([tableId, layoutData]) => {
            const id = parseInt(tableId, 10);
            const dbTable = dbTableMap[id];
            if (!dbTable) return null;
            const selId = `table-${id}`;
            return (
              <TableElement
                key={tableId}
                tableId={id}
                layoutData={layoutData}
                dbTable={dbTable}
                zoom={zoom}
                activeFilter={activeFilter}
                isSelected={selectedElementId === selId}
                isHighlighted={highlightedTableIds.has(id)}
                highlightedGuestId={highlightedGuestId}
                onClick={() => { onSelectElement(selId); onTableClick(id); }}
                onRename={(name) => onRenameTable(id, name)}
                onResize={(nx, ny, nw, nh) => onResizeElement(id, nx, ny, nw, nh, true)}
                onResizeEnd={(nx, ny, nw, nh) => onResizeElement(id, nx, ny, nw, nh, true)}
                onRotate={(angle) => onRotateElement?.(id, angle, true)}
              />
            );
          })}
        </Box>
      </Box>

      {/* "Drop here" visual indicator for palette items */}
      {isOver && activeDragData?.type === 'PALETTE_ITEM' && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          border: '2px dashed #2a5d32',
          borderRadius: 2,
          pointerEvents: 'none',
          bgcolor: 'rgba(42,93,50,0.05)',
        }} />
      )}

      {/* Trash zone – shown when dragging a canvas element */}
      {isDraggingCanvasElement && (
        <Box
          ref={setTrashRef}
          sx={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 1,
            borderRadius: 2,
            border: isOverTrash ? '2px solid #f44336' : '2px dashed #ef9a9a',
            bgcolor: isOverTrash ? 'rgba(244,67,54,0.15)' : 'rgba(244,67,54,0.06)',
            transition: 'all 0.15s',
            zIndex: 200,
            pointerEvents: 'all',
            cursor: 'copy',
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18, color: isOverTrash ? '#f44336' : '#ef9a9a' }} />
          <Typography variant="caption" sx={{ color: isOverTrash ? '#f44336' : '#ef9a9a', fontWeight: 600 }}>
            Ide húzva töröl
          </Typography>
        </Box>
      )}
    </Box>
  );
}
